import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MoveHorizontal, Loader2, Calendar as CalendarIcon, X } from 'lucide-react';
import { getFirstImageCandidate, getDailyImages } from '../../api/iframeConfig.api';
import { getLatestImage } from '../../api/lastImage.api';
import Calendar from '../calendar/Calendar.js';
import { getRotationDegrees } from '../../../utils/cameraOrientation.js';
import { useOrientedMedia } from '../../../utils/useOrientedMedia.js';
import useSyncedZoom, { ZoomIndicator } from './useSyncedZoom';
import { useFramingCheck, alignmentTransform, ahoraPrecalculado } from './frameAlign';
import FramingNotice, { FramingLoading } from './FramingNotice';

export const formatDateEs = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// firstImage.date / lastImage.date come from different endpoints and aren't
// guaranteed to share a format (plain "YYYY-MM-DD" vs. full ISO timestamp),
// so parse directly instead of concatenating a time suffix onto them.
export const parseDateSafe = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
};

// Lets the user browse month-by-month (Calendar fetches one month of
// availability at a time) and then loads only that day's images on demand,
// instead of fetching every image up front.
const preloadImages = (urls) => Promise.all(
  urls.map((url) => new Promise((resolve) => {
    const img = new Image();
    img.onload = resolve;
    img.onerror = resolve;
    img.src = url;
  }))
);

export const useDailyImagePicker = (feedId) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pickerDate, setPickerDate] = useState(null);
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const selectDate = useCallback(async (date) => {
    if (!date || isNaN(date.getTime())) return;
    const requestId = ++requestIdRef.current;
    setPickerDate(date);
    setIsLoading(true);
    setError(null);
    setImages([]);
    try {
      const dateKey = date.toISOString().slice(0, 10);
      const data = await getDailyImages(feedId, dateKey);
      const items = Array.isArray(data) ? data : [];
      const parsed = items
        .map((item) => (typeof item === 'string' ? { url: item, date: dateKey } : { url: item?.url, date: item?.date || dateKey }))
        .filter((item) => item.url);

      // Wait for every thumbnail to be fully decoded before showing the
      // grid, otherwise images pop in one by one as they stream from the
      // server (progressive JPEG top-to-bottom effect).
      await preloadImages(parsed.map((item) => item.url));

      if (requestIdRef.current !== requestId) return;
      setImages(parsed);
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      setError('No se pudieron cargar las imágenes de ese día.');
      setImages([]);
    } finally {
      if (requestIdRef.current === requestId) setIsLoading(false);
    }
  }, [feedId]);

  const open = useCallback((initialDate) => {
    setIsOpen(true);
    if (initialDate) selectDate(initialDate);
  }, [selectDate]);

  const close = useCallback(() => setIsOpen(false), []);

  return { isOpen, open, close, pickerDate, images, isLoading, error, selectDate };
};

export const DatePickerModal = ({ title, user, picker, onSelectImage, onClose }) => (
  <div
    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]"
    onClick={(e) => e.target === e.currentTarget && onClose()}
  >
    <div className="bg-white rounded-lg w-full max-w-lg p-6 shadow-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-800">{title}</h2>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-3">
        <Calendar user={user} selected={picker.pickerDate} onDateSelect={picker.selectDate} />

        {picker.isLoading && (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            Cargando imágenes...
          </div>
        )}
        {picker.error && <p className="text-sm text-red-600">{picker.error}</p>}
        {!picker.isLoading && !picker.error && picker.pickerDate && picker.images.length === 0 && (
          <p className="text-sm text-gray-500">No hay imágenes para ese día.</p>
        )}

        <div className="grid grid-cols-3 gap-2">
          {!picker.isLoading && picker.images.map((image, index) => (
            <button
              key={`${image.url}-${index}`}
              type="button"
              onClick={() => onSelectImage(image)}
              className="relative aspect-video rounded overflow-hidden border border-gray-200 hover:ring-2 hover:ring-blue-500"
            >
              <img src={image.url} alt={`Opción ${index + 1}`} className="absolute inset-0 w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const FirstLastImageCompare = ({ user, onGoToFirstImage }) => {
  const feedId = user?.feedID;
  const rotationDeg = getRotationDegrees(user?.orientation);
  const [firstImage, setFirstImage] = useState(null);
  const [firstImageAccepted, setFirstImageAccepted] = useState(false);
  const [lastImage, setLastImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [imagesReady, setImagesReady] = useState(false);
  const [sliderPos, setSliderPos] = useState(50);
  const { containerRef, nodeRef, captureNaturalSize, display: orientedDisplay } = useOrientedMedia(rotationDeg);
  const visibleBounds = orientedDisplay?.visibleBounds || { leftPercent: 0, widthPercent: 100 };

  // Zoom sincronizado: las dos fotos viven en el mismo "stage" transformado.
  // El divisor queda en coordenadas de pantalla (no se agranda con el zoom),
  // así que el recorte del "antes" se recalcula en coordenadas del stage.
  const beforeClipRef = useRef(null);
  const sliderPosRef = useRef(sliderPos);
  const visibleBoundsRef = useRef(visibleBounds);
  sliderPosRef.current = sliderPos;
  visibleBoundsRef.current = visibleBounds;
  const applyClip = useCallback((z, animate = false) => {
    const clipEl = beforeClipRef.current;
    const width = clipEl?.parentElement?.offsetWidth;
    if (!clipEl || !width) return;
    const vb = visibleBoundsRef.current;
    const screenPx = ((vb.leftPercent + (sliderPosRef.current / 100) * vb.widthPercent) / 100) * width;
    const stagePercent = Math.min(100, Math.max(0, (((screenPx - z.x) / z.s) / width) * 100));
    clipEl.style.transition = animate ? 'clip-path 200ms ease-out' : 'none';
    clipEl.style.clipPath = `inset(0 ${100 - stagePercent}% 0 0)`;
  }, []);
  const zoom = useSyncedZoom({ onChange: applyClip });
  const zoomViewportRef = zoom.viewportRef;
  const setViewport = useCallback((el) => {
    containerRef(el);
    zoomViewportRef(el);
  }, [containerRef, zoomViewportRef]);

  const beforePicker = useDailyImagePicker(feedId);
  const afterPicker = useDailyImagePicker(feedId);
  const [beforeIsDefault, setBeforeIsDefault] = useState(true);
  const setBeforeFromCheck = useCallback((image) => setFirstImage({ url: image.url, date: image.date }), []);
  const framing = useFramingCheck({ feedId, before: firstImage, after: lastImage, setBefore: setBeforeFromCheck, isDefaultBefore: beforeIsDefault });

  useEffect(() => {
    if (!feedId) {
      setIsLoading(false);
      return;
    }
    let isMounted = true;
    setIsLoading(true);

    Promise.allSettled([getFirstImageCandidate(feedId), getLatestImage(feedId), ahoraPrecalculado(feedId)]).then(
      ([firstResult, lastResult, ahoraPre]) => {
        if (!isMounted) return;

        if (firstResult.status === 'fulfilled') {
          setFirstImage({ url: firstResult.value.url, date: firstResult.value.date });
          setFirstImageAccepted(Boolean(firstResult.value.accepted));
        }

        if (lastResult.status === 'fulfilled') {
          const response = lastResult.value;
          const url = typeof response === 'object' ? response?.url : response;
          const date = typeof response === 'object' ? response?.date : null;
          if (url) setLastImage({ url, date });
        }
        // Par precalculado: "ahora" = la foto ya verificada contra "antes" (nunca un par desalineado).
        if (ahoraPre.status === 'fulfilled' && ahoraPre.value) setLastImage(ahoraPre.value);

        setIsLoading(false);
      }
    );

    return () => {
      isMounted = false;
    };
  }, [feedId]);

  // Preload both photos fully off-screen so the visible <img> tags mount
  // already-decoded from cache, instead of streaming in as a progressive JPEG.
  useEffect(() => {
    if (!firstImage || !lastImage) return;

    let isMounted = true;
    let firstDone = false;
    let lastDone = false;
    setImagesReady(false);

    const checkDone = () => {
      if (firstDone && lastDone && isMounted) setImagesReady(true);
    };

    const firstImg = new Image();
    firstImg.onload = () => { firstDone = true; checkDone(); };
    firstImg.onerror = () => { firstDone = true; checkDone(); };
    firstImg.src = firstImage.url;

    const lastImg = new Image();
    lastImg.onload = () => {
      lastDone = true;
      if (isMounted) captureNaturalSize(lastImg);
      checkDone();
    };
    lastImg.onerror = () => { lastDone = true; checkDone(); };
    lastImg.src = lastImage.url;

    return () => {
      isMounted = false;
    };
  }, [firstImage, lastImage, captureNaturalSize]);

  useLayoutEffect(() => {
    applyClip(zoom.state.current);
  });

  // The handle drags over the whole container, but the draggable range is
  // the image's own bounds — over the black bars (when letterboxed) the
  // position just clamps at the nearest image edge instead of continuing
  // to move the slider.
  const updatePosition = useCallback((clientX) => {
    const rect = nodeRef.current?.getBoundingClientRect();
    if (!rect) return;
    const imageLeft = rect.left + (visibleBounds.leftPercent / 100) * rect.width;
    const imageWidth = (visibleBounds.widthPercent / 100) * rect.width;
    if (imageWidth <= 0) return;
    const percent = ((clientX - imageLeft) / imageWidth) * 100;
    setSliderPos(Math.min(100, Math.max(0, percent)));
  }, [nodeRef, visibleBounds.leftPercent, visibleBounds.widthPercent]);

  const toggleBeforePicker = () => {
    if (beforePicker.isOpen) {
      beforePicker.close();
      return;
    }
    beforePicker.open(parseDateSafe(firstImage?.date) || new Date());
  };

  const toggleAfterPicker = () => {
    if (afterPicker.isOpen) {
      afterPicker.close();
      return;
    }
    afterPicker.open(parseDateSafe(lastImage?.date) || new Date());
  };

  const handleSelectBeforeImage = (image) => {
    framing.userPicked();
    setBeforeIsDefault(false);
    setFirstImage({ url: image.url, date: image.date });
    setFirstImageAccepted(true);
    beforePicker.close();
  };

  const handleSelectAfterImage = (image) => {
    framing.userPicked();
    setLastImage({ url: image.url, date: image.date });
    afterPicker.close();
  };

  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    const handleMove = (moveEvent) => {
      const clientX = moveEvent.touches ? moveEvent.touches[0].clientX : moveEvent.clientX;
      updatePosition(clientX);
    };
    const handleEnd = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove);
    window.addEventListener('touchend', handleEnd);
  }, [updatePosition]);

  if (!isLoading && (!firstImage || !lastImage)) {
    return (
      <div className="w-full aspect-video bg-gray-100 rounded-xl border border-dashed border-gray-300 flex flex-col items-center justify-center text-center gap-2 p-4">
        <p className="text-sm text-gray-600">
          Seleccioná la primera imagen para mostrarte el comparador de avance de obra.
        </p>
        <button
          type="button"
          onClick={onGoToFirstImage}
          className="text-blue-600 hover:underline font-medium text-sm"
        >
          Seleccionar primer imagen
        </button>
      </div>
    );
  }

  if (!isLoading && imagesReady && framing.checking) {
    return <FramingLoading searching={framing.searching} progress={framing.progress} />;
  }

  if (isLoading || !imagesReady) {
    return (
      <div className="w-full aspect-video bg-gray-300 rounded-lg relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-gray-500" />
        </div>
      </div>
    );
  }

  const fallbackImgStyle = { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' };
  const orientedImgStyle = orientedDisplay?.style || fallbackImgStyle;

  return (
    <div>
      <div
        ref={setViewport}
        tabIndex={0}
        aria-label="Comparador antes y ahora. Rueda del mouse o pellizco para hacer zoom, + y - con el teclado, doble clic para volver."
        className={`relative w-full aspect-video rounded-xl overflow-hidden select-none shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${orientedDisplay?.isVertical ? 'bg-black' : ''}`}
      >
        <div ref={zoom.stageRef} className="absolute inset-0" style={{ transformOrigin: '0 0' }}>
          <img src={lastImage.url} alt="Última foto" style={orientedImgStyle} draggable={false} />
          <div ref={beforeClipRef} className="absolute inset-0">
            <img src={firstImage.url} alt="Primera foto" style={{ ...orientedImgStyle, transform: alignmentTransform(framing.alignment, orientedImgStyle.transform) }} draggable={false} />
          </div>
        </div>

        <div className="absolute top-2 left-2 pointer-events-none bg-black/60 text-white text-xs px-2 py-1 rounded">
          Primera foto{firstImage.date ? ` · ${formatDateEs(firstImage.date)}` : ''}
        </div>
        <div className="absolute top-2 right-2 pointer-events-none bg-black/60 text-white text-xs px-2 py-1 rounded">
          Última foto{lastImage.date ? ` · ${formatDateEs(lastImage.date)}` : ''}
        </div>

        <div
          className="absolute inset-y-0"
          style={{ left: `${visibleBounds.leftPercent + (sliderPos / 100) * visibleBounds.widthPercent}%`, transform: 'translateX(-50%)' }}
        >
          <div className="w-0.5 h-full bg-white shadow-md mx-auto" />
          <div
            data-zoom-ignore
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 bg-white rounded-full shadow-md flex items-center justify-center cursor-ew-resize"
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
          >
            <MoveHorizontal className="w-5 h-5 text-gray-700" />
          </div>
        </div>
        <ZoomIndicator ref={zoom.indicatorRef} onReset={() => zoom.reset(true)} />
      </div>

      <FramingNotice framing={framing} />

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <button
            type="button"
            onClick={toggleBeforePicker}
            className="flex items-center gap-1 min-h-[44px] lg:min-h-0 text-sm text-blue-600 hover:underline font-medium"
          >
            <CalendarIcon className="w-4 h-4" />
            Elegir otra fecha (primera foto)
          </button>

          {beforePicker.isOpen && (
            <DatePickerModal
              title="Elegir otra fecha · Primera foto"
              user={user}
              picker={beforePicker}
              onSelectImage={handleSelectBeforeImage}
              onClose={beforePicker.close}
            />
          )}
        </div>

        <div>
          <button
            type="button"
            onClick={toggleAfterPicker}
            className="flex items-center gap-1 min-h-[44px] lg:min-h-0 text-sm text-blue-600 hover:underline font-medium"
          >
            <CalendarIcon className="w-4 h-4" />
            Elegir otra fecha (última foto)
          </button>

          {afterPicker.isOpen && (
            <DatePickerModal
              title="Elegir otra fecha · Última foto"
              user={user}
              picker={afterPicker}
              onSelectImage={handleSelectAfterImage}
              onClose={afterPicker.close}
            />
          )}
        </div>
      </div>

      {!firstImageAccepted && onGoToFirstImage && (
        <p className="mt-2 text-xs text-gray-500">
          Si la primer imagen no es correcta,{' '}
          <button
            type="button"
            onClick={onGoToFirstImage}
            className="text-blue-600 hover:underline font-medium"
          >
            seleccioná la correcta acá
          </button>
          .
        </p>
      )}
    </div>
  );
};

export default FirstLastImageCompare;
